import { Alert, Button, Input } from "reactstrap";

import { Drawer } from "../components/Drawer";
import PropTypes from "prop-types";
import Select from "react-select";
import { useState } from "react";

// The Keycloak service account UUID is typed by hand for now, so reject here what
// Postgres would refuse on a uuid column (500 instead of a readable error).
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const ApplicationDrawer = ({
  application,
  companies,
  onClose,
  onSubmit,
}) => {
  const isEdition = !!application.id;
  const [name, setName] = useState(application.name ?? "");
  const [keycloakUuid, setKeycloakUuid] = useState(
    application.keycloak_uuid ?? ""
  );
  const [active, setActive] = useState(application.active ?? true);
  const [companyId, setCompanyId] = useState(application.company?.id ?? null);
  const [alert, setAlert] = useState("");
  const [saving, setSaving] = useState(false);

  const trimmedUuid = keycloakUuid.trim();
  const uuidIsValid = UUID_PATTERN.test(trimmedUuid);
  const canSubmit = !!name.trim() && !!companyId && uuidIsValid && !saving;

  const companyOptions = companies.map(({ id, name: label }) => ({
    value: id,
    label,
  }));

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit(companyId, {
        name: name.trim(),
        // Canonical form: the uuid column stores lowercase, so send lowercase
        keycloak_uuid: trimmedUuid.toLowerCase(),
        active,
      });
    } catch (error) {
      setAlert(String(error));
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button color="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button color="primary" disabled={!canSubmit} onClick={submit}>
        {isEdition ? "Save" : "Create"}
      </Button>
    </>
  );

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={isEdition ? `Edit ${application.name}` : "New application"}
      footer={footer}
    >
      <Alert color="danger" isOpen={!!alert} toggle={() => setAlert("")}>
        {alert}
      </Alert>

      <label className="cf-field w-100 mb-3">
        <span className="cf-field__label">Company</span>
        {isEdition ? (
          <Input plaintext readOnly value={application.company.name} />
        ) : (
          <Select
            className="cf-rs"
            classNamePrefix="cf-rs"
            options={companyOptions}
            value={companyOptions.find((option) => option.value === companyId)}
            onChange={(picked) => setCompanyId(picked?.value ?? null)}
            placeholder="Select a company…"
            aria-label="Company"
          />
        )}
      </label>

      <label className="cf-field w-100 mb-3">
        <span className="cf-field__label">Name</span>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Application name"
        />
      </label>

      <label className="cf-field w-100 mb-3">
        <span className="cf-field__label">Keycloak UUID</span>
        <Input
          value={keycloakUuid}
          invalid={!!trimmedUuid && !uuidIsValid}
          onChange={(event) => setKeycloakUuid(event.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
        />
        <small className="text-muted">
          Service account UUID of the Keycloak client, to copy manually from
          Keycloak.
        </small>
      </label>

      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="application-active"
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />
        <label className="form-check-label" htmlFor="application-active">
          Active
        </label>
      </div>
    </Drawer>
  );
};

ApplicationDrawer.propTypes = {
  // Empty object (minus company) when creating
  application: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    keycloak_uuid: PropTypes.string,
    active: PropTypes.bool,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  }).isRequired,
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
